import { Autocomplete, Stack, TextField, Typography, Popper, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useTBContext } from "../../Contexts/useTBStates";
import { IOSSwitch } from "../../../shared/Components/iosSwitch";
import { StyledIconButton } from "../../../shared/Components/StyledIconButton";
import DeleteIcon from '@mui/icons-material/Delete';


interface FlightingItemDisplayProps {
    index: number;
    orient: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    label: string;
    keyName: string;
    level: 'division' | 'flight';
    labelField?: string;
    optionsKeyName?: string;
    freeSolo?: boolean;
    hideLabel?: boolean;
    align?: 'left' | 'center' | 'right';
    extraFactor?: number | 1;
    locked?: boolean;
    removable?: boolean;

}


export const FlightingItemDisplay = ({ 
       index, orient, label, keyName, level, labelField, optionsKeyName, freeSolo, hideLabel, align, extraFactor, locked, removable,
}: FlightingItemDisplayProps) => {



    const { viewEvent, setViewEvent,
        factor,
        appTheme, 
        editMode,
        forceUpdate,
        optionsLoaded,
    } = useTBContext();
    
    const optionsEntry = optionsLoaded.find((o: unknown) => (o as { keyName: string }).keyName === optionsKeyName) as { options?: Record<string, unknown>[] } | undefined;
    const thisOptions: Record<string, unknown>[] = optionsEntry?.options ?? [];

    function formatValue() {
        if (!viewEvent) return null;
        let newValue: any;

        if (level === 'division') {
            const found = viewEvent.flighting.divisions.find((d: any) => d.index === index);
            newValue = found && keyName in found ? found[keyName] : null;
        } else if (level === 'flight') {
            const found = viewEvent.flighting.flights.find((f: any) => f.index === index);
            newValue = found && keyName in found ? found[keyName] : null;
        }

        return newValue;
    }


    function getDataType() {
        const value = formatValue();
        if (typeof value === 'number' || (typeof value === 'string' && /^-?\d*\.?\d+$/.test(value.trim()))) {
            return 'number';
        } else if (typeof value === 'boolean') {
            return 'boolean';
        }
        return 'string';
    }

    function updateFlighting(newValue: any) {
        if (!viewEvent) return;
        const updatedFlighting = JSON.parse(JSON.stringify(viewEvent.flighting));

        if (level === 'division') {

            updatedFlighting.divisions = updatedFlighting.divisions.map((d: any, i: number) => {
                if (i === index) {
                    return {
                        ...d,
                        [keyName]: newValue

                    };
                }
                return d;
            });

            setViewEvent(prev => prev ? { ...prev, flighting: JSON.parse(JSON.stringify(updatedFlighting)) } : null);



        } else if (level === 'flight') {

            updatedFlighting.flights = updatedFlighting.flights.map((f: any, i: number) => {
                if (i === index) {

                    return {
                        ...f,
                        [keyName]: newValue
                    };
                }

                return f;
            });

            setViewEvent(prev => prev ? { ...prev, flighting: JSON.parse(JSON.stringify(updatedFlighting)) } : null);

        }
    }

    const [displayValue, setDisplayValue] = useState<any>(formatValue());
    const [dataType, setDataType] = useState<string>(getDataType());


    useEffect(() => {
        setDisplayValue(formatValue());
    }, [forceUpdate]);

    return (
        <>
            {editMode && !locked ? (
                <Stack direction={orient} spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'center',
                    minWidth: keyName === 'flightName' ? '120px' : '0px'
                 }}>
                    {!hideLabel && 
                        <Typography 
                            flex={1} 
                            width={orient === 'row' ? 'auto' : '100%'} 
                            textAlign={orient === 'row' ? 'right' : 'left'} 
                            sx={{ 
                                fontSize: 14*factor*(extraFactor || 1), 
                                fontWeight: 500 
                            }}>
                            {label}:
                        </Typography>
                    }
                    {thisOptions && thisOptions.length > 0 && labelField && (
                           
                                <Autocomplete
                                fullWidth
                                freeSolo={freeSolo || false}
                                disableClearable={true}
                                options={thisOptions}
                                getOptionLabel={(option: string | Record<string, unknown>) => typeof option === 'string' ? option : String(option[labelField] ?? '')}
                                value={thisOptions.find((o) => o[labelField] === displayValue) ?? undefined}
                                onChange={(e, newValue: string | Record<string, unknown> | null) => {
                                    if (newValue == null) return;
                                    console.log('New Value', newValue);
                                    const val = typeof newValue === 'string' ? newValue : (newValue[labelField] ?? '');
                                    setDisplayValue(val);
                                    updateFlighting(val);
                                }}
                                sx={{
                                    flex: 1,
                                    padding: 0,
                                    '& .MuiAutocomplete-inputRoot': {
                                        padding: 0,
                                    }
                                }}
                                renderInput={(params) => <TextField 
                                    {...params} 
                                    variant="outlined" 
                                    sx={{
                                        padding: 0,
                                        "& .MuiInputBase-input": { fontSize: 14*factor*(extraFactor || 1), height: 14*factor*(extraFactor || 1), padding: 1 },
                                        '& .MuiOutlinedInput-root': {padding: '0px 0px'},
                                        '& input': {textAlign: 'left', lineHeight: 'normal'},
                                        flex: 1,
                                        width: '100%'
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        inputMode: "text",
                                        autoComplete: "off",
                                        sx: {
                                            fontSize: 14 * (factor ? factor : 1) ,
                                            /* '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: appTheme.palette.primary.main,
                                            }, */
                                        },
                                    }}
                                    InputLabelProps={{
                                        sx: { fontSize: 16 * (factor ? factor : 1) * (extraFactor || 1), lineHeight: 'normal', padding: '0px 10px', backgroundColor: 'whitesmoke', marginTop: '0px' },
                                        shrink: true
                                    }}
                                />}
                                PopperComponent={(props) => (
                                    <Popper {...props} autoFocus sx={{ 
                                        zIndex: 1300, // Ensure it appears above other elements
                                        '& .MuiAutocomplete-listbox': {
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            bgcolor: 'whitesmoke',
                                        },
                                        '& .MuiAutocomplete-option': {
                                            padding: '5px 10px',
                                            '&:hover': {
                                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                                borderRadius: 3
                                            },
                                            fontSize: 14 * (factor ? factor : 1),
                                        },
                                        '& .Mui-focusVisible' : {
                                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                                borderRadius: 3,
                                        },
                                        
                                    }} />
                                    
                            )}
                        />
                        
                    )}
                    {(!thisOptions || thisOptions.length === 0) && dataType !== 'boolean' && (
                            <TextField
                                value={displayValue}
                                type={dataType}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    console.log(newValue);
                                    const formattedValue = dataType === 'number' ? Number(newValue) : newValue;
                                    updateFlighting(formattedValue);
                                    setDisplayValue(dataType === 'number' ? formattedValue : formattedValue);
                        
                                }}
                                sx={{
                                    padding: 0,
                                    "& .MuiInputBase-input": { fontSize: 14*factor*(extraFactor || 1), height: 14*factor*(extraFactor || 1), padding: 1 },
                                    '& .MuiOutlinedInput-root': {padding: '0px 0px'},
                                    '& input': {
                                        textAlign: align,
                                        lineHeight: 'normal'},
                                }}
                                InputProps={{
                                    sx: {
                                        fontSize: 14*factor*(extraFactor || 1),
                                    },
                                }}
                            />

                        )}
                    {dataType === 'boolean' && (
                        <Stack direction='row' flex={1} spacing={1} alignItems='center' justifyContent='center' >
                            {/* <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>No</Typography> */}
                            <IOSSwitch
                                checked={displayValue}
                                onChange={(e) => {
                                    setDisplayValue(e.target.checked);
                                    updateFlighting(e.target.checked);
                                }}

                            />
                            {/* <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>Yes</Typography> */}
                        </Stack>
                    )}
                    {removable && (
                    <StyledIconButton
                            icon={<DeleteIcon />}
                            onClick={() => {
                                setDisplayValue("<removed>");
                                setViewEvent((prevState: any) => ({...prevState, [keyName]: "<removed>"}));
                            }}
                            appTheme={appTheme}
                            label=""
                            factor={factor}
                            color="error"
                            width={'max-content'}
                        />)}
                    </Stack>
            ) : 
            (
            <Stack direction={orient} spacing={orient === 'row' ? 1 : 0.5} 
                sx={{ width: '100%', justifyContent: 'flex-start', alignItems: 'center',
                    minWidth: keyName === 'flightName' ? '120px' : 'max-content'
            }}>
                {!hideLabel && <Typography 
                    flex={1} 
                    width={orient === 'row' ? 'auto' : '100%'} 

                    textAlign={orient === 'row' ? 'right' : 'left'} 
                    padding={0}
                    sx={{ 
                        fontWeight: 500, 
                        fontSize: 14*factor*(extraFactor || 1),
                        minHeight: 24*factor*(extraFactor || 1),
                    }}>
                    {label}:
                </Typography>}
                {dataType !== 'boolean' && (
                    <Typography 
                        flex={1} 
                        width={orient === 'row' ? 'auto' : '100%'} 
                        textAlign={align} 
                        padding={0.5} 
                        sx={{ 
                            fontSize: 14*factor*(extraFactor || 1),
                            minHeight: 24*factor*(extraFactor || 1),                            
                        }}>

                        {displayValue}
                    </Typography>

                )}
                {dataType === 'boolean' && (
                    <Stack direction='row' flex={1}  spacing={1} alignItems='center' justifyContent='center'>
                        {/* <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>No</Typography> */}
                        <IOSSwitch
                            checked={displayValue}
                            disabled
                            onChange={(e) => {
                                setDisplayValue(e.target.checked);
                                updateFlighting(e.target.checked);
                            }}

                        />
                        {/* <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>Yes</Typography> */}
                    </Stack>
                )}
            </Stack>
            )}
        </>
    )
}
